import React from 'react';
import styled from 'styled-components';
import Select from '@mui/material/Select';
import { InputBase, MenuItem, SelectChangeEvent } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';

const Wrapper = styled.div<{ disabled?: boolean, hasError?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  border-radius: 4px;
  transition: all 0.3s ease;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};

  &:hover {
    border-color: ${props => props.hasError ? 'red' : '#000'};
  }
`;

const OuterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const Icon = styled.img`
  width: 20px;
  height: auto;
  margin: 0 10px 0 2px;
`;

const StyledSelect = styled(Select)<{ disabled?: boolean }>`
  width: 100%;

  :hover {
    border: none;
  }
`;

const Container = styled.div`
  flex: 1;
  width: 100%;
  display: flex;
  flex-direction: row;
  height: 28px;
  justify-content: flex-start;
  align-items: center;
`;

const Placeholder = styled.span`
  font-size: 16px;
  font-weight: 300;
  color: ${({ theme }) => theme.colors.basic400};
  outline: none;
  border: none;
    font-family: "Figtree", serif;
`;

const Text = styled.span`
  font-size: 16px;
  font-weight: 300;
  color: ${({ theme }) => theme.colors.basic1000};
  outline: none;
  border: none;
    font-family: "Figtree", serif;
`;

const BootstrapInput = styled(InputBase)`
  font-size: 16px;
  padding: 14px 16px;
  border-bottom: 2px solid #FFF200;
  font-family: "Figtree", serif;

  @media (orientation: portrait) and (max-width: 767px) {
    padding: 8px 16px;
  }

  &:hover {
    border-bottom-color: #95D0DD;
  }

  &:focus-within {
    border-color: #95d0dd;
  }
`;

const Error = styled.span`
  color: red;
  font-size: 12px;
  margin-top: 5px;
`;

export type Option = {
  value: string;
  label: string;
}

export interface InputSelectProps {
  options: Option[];
  value: string | undefined;
  handleChange: (event: SelectChangeEvent<any>) => void;
  onBlur?: () => void;
  placeholder: string;
  icon?: string;
  disableClearable?: boolean;
  isSearchable?: boolean;
  disabled?: boolean;
  error?: string;
}

const InputSelect = ({ options, value, handleChange, onBlur, placeholder, icon, disableClearable = false, disabled = false, error }: InputSelectProps) => {
  const Value = (value: any): React.ReactNode => (
    <Container>
      {!!icon && <Icon src={icon} alt="Icon" />}
      {value === '' ? (
        <Placeholder>{placeholder}</Placeholder>
      ) : <Text>{options.find((opt) => opt.value === value)?.label}</Text>}
    </Container>
  );

  return (
    <OuterWrapper>
      <Wrapper hasError={!!error} disabled={disabled}>
        <StyledSelect
          value={value}
          onChange={handleChange}
          onBlur={onBlur}
          style={{ outline: 'none' }}
          renderValue={Value}
          displayEmpty
          input={<BootstrapInput />}
          disabled={disabled}
          disableUnderline
          fullWidth
          IconComponent={() => <ExpandMore />}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 300
              }
            }
          }}
        >
          <MenuItem value="" disabled>
            <em>{placeholder}</em>
          </MenuItem>
          {options.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </StyledSelect>
      </Wrapper>
      {!!error && <Error>{error}</Error>}
    </OuterWrapper>
  );
};

export default InputSelect;
